import type { Connection, Edge, Node } from '@xyflow/react'
import {
  getUnderlyingEdgeIds,
  getUnderlyingInputEdgeIds,
  isProxyEdge,
  parseOpenInputHandle,
  resolveGroupOutputSource,
} from '@/features/backtest/utils/collapse-model'
import { isPassthroughType } from '@/features/backtest/utils/inline-passthroughs'

/**
 * The SINGLE translation surface between the collapsed-subnet PROXY view and the
 * canonical graph.
 *
 * A collapsed subnet renders boundary handles (`open-in:…`, `proxy-in:…`,
 * `proxy-out:…`) and synthetic proxy wires. Every edge OPERATION on that view —
 * connect, delete (and, by extension, auto-connect, which just emits these same
 * handles) — must be re-expressed as a mutation of the CANONICAL graph (real
 * edges to the hidden subnetInput/subnetOutput pills). Historically each handler
 * re-derived that translation inline, and bugs were "an operation forgot to
 * translate." These pure resolvers own the decision once; handlers apply the
 * returned plan (snapshot + validate + re-project) but never re-derive it.
 *
 * Contract (proven by subnet-boundary-ops.unit.test.ts): a connection/deletion
 * expressed against a COLLAPSED boundary resolves to the SAME canonical mutation
 * as performing it directly against the pill in the EXPANDED subnet.
 */

export type BoundaryConnectionPlan =
  | {
      /** Not a boundary connection — the caller handles it normally. */
      kind: 'none'
    }
  | {
      /** Source was a group-output handle but the subnet has no single output. */
      kind: 'unresolved-output'
    }
  | {
      /** Add a new canonical edge (open input, or group-output → normal target). */
      kind: 'add'
      source: string
      sourceHandle: string | null
      target: string
      targetHandle: string | null
      /** True when the target is a pass-through pill → caller skips type validation. */
      targetIsPassthrough: boolean
    }
  | {
      /** Re-source the canonical edges behind a CONNECTED input row (proxy-in). */
      kind: 'rewire'
      underlyingEdgeIds: string[]
      source: string
      sourceHandle: string | null
    }

/**
 * Resolve a connection dropped on a collapsed subnet's boundary into the
 * canonical-graph mutation it represents. Pure — no React, no toasts.
 *
 * - `open-in:<pill>:in`  → ADD a canonical edge to that input pill.
 * - `proxy-in:<src>:<h>` → REWIRE the source of the edges feeding that input row.
 * - source `proxy-out:…` → resolve the subnet's real internal output, then ADD
 *   (to a normal target) — handled uniformly via the `add` kind.
 *
 * A `proxy-out:` SOURCE is resolved first, so it composes with any of the above
 * (e.g. subnet → subnet wires the internal output pill to the input pill).
 */
export function resolveBoundaryConnection(
  connection: Connection,
  nodes: Node[],
  edges: Edge[],
): BoundaryConnectionPlan {
  const targetHandle = connection.targetHandle ?? ''
  const sourceHandle = connection.sourceHandle ?? ''

  const open = parseOpenInputHandle(targetHandle)
  const targetsGroupInput = targetHandle.startsWith('proxy-in:')
  const sourcesGroupOutput = sourceHandle.startsWith('proxy-out:')

  if (!open && !targetsGroupInput && !sourcesGroupOutput) return { kind: 'none' }

  // Resolve a group-output SOURCE to its real internal (node, handle) up front,
  // so the rest of the decision is independent of whether the source was a
  // collapsed subnet.
  let source = connection.source ?? ''
  let resolvedSourceHandle: string | null = connection.sourceHandle ?? null
  if (sourcesGroupOutput) {
    const resolved = resolveGroupOutputSource(connection.source ?? '', nodes, edges)
    if (!resolved) return { kind: 'unresolved-output' }
    source = resolved.nodeId
    resolvedSourceHandle = resolved.handleId
  }

  if (open) {
    const pill = nodes.find((n) => n.id === open.nodeId)
    return {
      kind: 'add',
      source,
      sourceHandle: resolvedSourceHandle,
      target: open.nodeId,
      targetHandle: open.handleId,
      targetIsPassthrough: isPassthroughType(pill?.type),
    }
  }

  if (targetsGroupInput) {
    return {
      kind: 'rewire',
      underlyingEdgeIds: getUnderlyingInputEdgeIds(
        connection.target ?? '',
        targetHandle,
        nodes,
        edges,
      ),
      source,
      sourceHandle: resolvedSourceHandle,
    }
  }

  // sourcesGroupOutput with a NORMAL target.
  const target = nodes.find((n) => n.id === connection.target)
  return {
    kind: 'add',
    source,
    sourceHandle: resolvedSourceHandle,
    target: connection.target ?? '',
    targetHandle: connection.targetHandle ?? null,
    targetIsPassthrough: isPassthroughType(target?.type),
  }
}

/**
 * The canonical edge id(s) a deletion must remove. A proxy wire stands in for
 * hidden canonical edge(s); deleting the proxy alone leaves them and the next
 * re-projection rebuilds it, so a proxy must take its underlying edges with it.
 * Returns `[]` for a normal (non-proxy) edge — the caller deletes it as-is.
 */
export function resolveBoundaryDeletion(removedEdge: Edge, nodes: Node[], edges: Edge[]): string[] {
  if (!isProxyEdge(removedEdge)) return []
  return getUnderlyingEdgeIds(removedEdge, nodes, edges)
}

export type BoundaryReconnectPlan =
  | {
      /** Not a proxy edge — the caller reconnects it normally. */
      kind: 'none'
    }
  | {
      /** Tried to drag the GROUP-locked end of a proxy wire — refuse. The end
       *  that belongs to the collapsed group can't be rewired from outside;
       *  expand the subnet to rewire its interior. */
      kind: 'blocked'
    }
  | {
      /** Reconnect-through: rewire the moved end of the underlying canonical
       *  edge(s) the proxy projects over. `newSource`/`newTarget` is non-null
       *  only for the end that actually moved. */
      kind: 'rewire-through'
      underlyingEdgeIds: string[]
      newSource: { source: string; sourceHandle: string | null } | null
      newTarget: { target: string; targetHandle: string | null } | null
    }

/**
 * Resolve a drag-reconnect of a proxy wire into a rewire of the canonical
 * edge(s) it stands for. Dragging a proxy's EXTERNAL end rewires all 1..n
 * underlying edges (Nuke-style fan reconnect); dragging the GROUP end is
 * refused (`blocked`) since that end is the collapsed boundary, not a real
 * node. Pure — no React, no toasts.
 */
export function resolveBoundaryReconnect(
  oldEdge: Edge,
  newConnection: Connection,
  nodes: Node[],
  edges: Edge[],
): BoundaryReconnectPlan {
  if (!isProxyEdge(oldEdge)) return { kind: 'none' }

  const sourceMoved =
    newConnection.source !== oldEdge.source ||
    (newConnection.sourceHandle ?? null) !== (oldEdge.sourceHandle ?? null)
  const targetMoved =
    newConnection.target !== oldEdge.target ||
    (newConnection.targetHandle ?? null) !== (oldEdge.targetHandle ?? null)
  const sourceIsGroupEnd = (oldEdge.sourceHandle ?? '').startsWith('proxy-out:')
  const targetIsGroupEnd = (oldEdge.targetHandle ?? '').startsWith('proxy-in:')

  if ((sourceMoved && sourceIsGroupEnd) || (targetMoved && targetIsGroupEnd)) {
    return { kind: 'blocked' }
  }

  return {
    kind: 'rewire-through',
    underlyingEdgeIds: getUnderlyingEdgeIds(oldEdge, nodes, edges),
    newSource: sourceMoved
      ? { source: newConnection.source ?? '', sourceHandle: newConnection.sourceHandle ?? null }
      : null,
    newTarget: targetMoved
      ? { target: newConnection.target ?? '', targetHandle: newConnection.targetHandle ?? null }
      : null,
  }
}
